import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Zap, Camera, Scissors, Rocket } from "lucide-react";

const POINTS = [
  {
    icon: Zap,
    title: "Fast execution",
    copy: "Shoots lock in within 48 hours. Edits delivered in minutes, not hours.",
  },
  {
    icon: Camera,
    title: "Premium quality",
    copy: "Cinema-grade shots, color-graded edits, sound-designed cuts.",
  },
  {
    icon: Scissors,
    title: "Short-form native",
    copy: "Reels, TikToks, UGC, and vertical ads built to convert on feed.",
  },
  {
    icon: Rocket,
    title: "Built to grow",
    copy: "Content tuned for performance — not just looks. Designed to sell.",
  },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [10, 0, -6]);
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="relative py-28 md:py-40 perspective-scene"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#120202,transparent_60%)]" />
      <div
        aria-hidden
        className="absolute top-1/4 -right-40 h-[420px] w-[420px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #4ea8ff, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute top-2/3 -left-40 h-[360px] w-[360px] rounded-full blur-[120px] opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #ff3d8e, transparent 70%)" }}
      />
      <div className="neon-streak top-[8%] left-[-10%] right-[-10%]" />
      <div className="relative mx-auto max-w-7xl px-5">
        <motion.div
          style={{ rotateX }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16"
        >
          <div className="max-w-xl">
            <p className="mono-eyebrow mb-4" data-testid="about-eyebrow">
              Company
            </p>
            <h2
              data-testid="about-heading"
              className="font-display text-4xl md:text-6xl text-white leading-[1.02] tracking-tight"
            >
              A fast-moving studio for anyone who can't{" "}
              <span className="text-[#E10600]">wait weeks</span>.
            </h2>
          </div>
          <p
            className="max-w-md text-neutral-400 font-body text-base md:text-lg"
            data-testid="about-subcopy"
          >
            BlinkShots helps to create premium short-form visuals that capture attention and
            elevate your presence. We connect your occasion with skilled videographers,
            photographers, and editors — engineered for speed without losing craft.
          </p>
        </motion.div>

        <motion.div
          style={{ y }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
          data-testid="about-cards"
        >
          {POINTS.map((p, i) => (
            <AboutCard key={p.title} index={i} {...p} />
          ))}
        </motion.div>

        <div className="hairline-top mt-20 opacity-50" />

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {[
            { k: "24h", v: "Avg. booking confirmation" },
            { k: "10 min", v: "Edit turnaround" },
            { k: "120+", v: "Reels produced" },
            { k: "95%", v: "Client retention" },
          ].map((s) => (
            <div key={s.k} className="border-l border-white/10 pl-4">
              <p className="font-display text-3xl md:text-4xl text-white">{s.k}</p>
              <p className="mt-1 text-sm text-neutral-500 font-mono">{s.v}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutCard = ({ icon: Icon, title, copy, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40, rotateX: 12 }}
    whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
    viewport={{ once: true, margin: "-10%" }}
    transition={{ duration: 0.9, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ y: -6, rotateX: -3 }}
    className="tilt-card relative overflow-hidden rounded-3xl glass p-7 md:p-9 group"
    style={{ transform: `translateZ(${index * 12}px)` }}
    data-testid={`about-card-${index}`}
  >
    <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#E10600]/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="flex items-center gap-4 mb-5">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E10600]/10 border border-[#E10600]/20">
        <Icon className="h-5 w-5 text-[#E10600]" />
      </div>
      <span className="mono-eyebrow">0{index + 1}</span>
    </div>
    <h3 className="font-display text-2xl md:text-3xl text-white">{title}</h3>
    <p className="mt-3 text-neutral-400 font-body leading-relaxed">{copy}</p>
    <div className="mt-6 h-px w-16 bg-gradient-to-r from-[#E10600] to-transparent" />
  </motion.div>
);

export default About;
